/**
 * Admin authorization scopes + the deployment-environment stamp.
 *
 * TWO SEPARATE THINGS live here because they are always used together at
 * the same gate:
 *
 *   1. WHAT an admin may do        → ADMIN_SCOPES
 *   2. WHICH deployment they may   → deployEnv() / KeyEnv
 *      do it against
 *
 * ── Roles vs scopes ────────────────────────────────────────────────────
 *
 * `adminUsers.role` predates this module and stays the TIER:
 *
 *   • "admin"     — superadmin. Holds every scope implicitly, forever,
 *                   including ones added after their row was created, and
 *                   has reign over EVERY synthetic account regardless of
 *                   which admin created it. This is the genesis account.
 *   • "moderator" — scoped admin. Holds exactly the scopes granted at
 *                   creation time and nothing else.
 *
 * `scopes` is therefore only ever consulted for "moderator" rows. Asking
 * whether a superadmin "has" a scope always answers yes — never special-
 * case that at a call site, always go through `adminHasScope`.
 *
 * MIGRATION NOTE: before this module, `role` gated only three endpoints
 * (admin create/update/list in admin/users.ts) and a "moderator" could do
 * everything else. Introducing scopes RESTRICTS those existing rows, so
 * the backfill grants every pre-existing moderator the full scope set —
 * their effective permissions are unchanged until someone edits them.
 * New admins start from an explicitly chosen list.
 */

export const ADMIN_SCOPES = [
  "users:read",
  "users:write",
  "moderation:write",
  "reports:write",
  "payments:read",
  "support:write",
  "flags:write",
  "admins:write",
  // Synthetic (API-created) accounts. `synthetic:purge` authorizes the
  // ACTION; whose accounts it may touch is a separate question answered by
  // canPurgeSynthetic below — a scoped admin holding this scope can still
  // only purge what they themselves created.
  "synthetic:create",
  "synthetic:purge",
  // Mint/rotate/revoke one's OWN admin API keys. Never someone else's:
  // there is deliberately no scope that grants authority over another
  // admin's keys, because that would let a scoped admin escalate by
  // minting a key belonging to a superadmin.
  "apikeys:mint",
  // Read the API reference + OpenAPI document.
  "docs:read",
  // The SEO blog: read every post (drafts included) / write posts, pillars
  // and images. Blog content never touches user data, so these are
  // independent of every users:* and synthetic:* scope.
  "blog:read",
  "blog:write",
] as const;

export type AdminScope = (typeof ADMIN_SCOPES)[number];

/**
 * How the console groups scopes in every picker (API key mint/edit, new
 * admin). Defined here, next to ADMIN_SCOPES, so a new scope cannot ship
 * without a home: tests/adminScopeGroups.test.ts fails unless every scope
 * sits in exactly one group. Order is display order.
 */
export const ADMIN_SCOPE_GROUPS: readonly {
  id: string;
  label: string;
  description: string;
  scopes: readonly AdminScope[];
}[] = [
  {
    id: "users",
    label: "Users & safety",
    description: "View and act on user accounts, moderation and reports.",
    scopes: ["users:read", "users:write", "moderation:write", "reports:write"],
  },
  {
    id: "support",
    label: "Support & billing",
    description: "Answer support threads and view billing.",
    scopes: ["support:write", "payments:read"],
  },
  {
    id: "synthetic",
    label: "Test accounts",
    description: "Create and delete synthetic test accounts.",
    scopes: ["synthetic:create", "synthetic:purge"],
  },
  {
    id: "blog",
    label: "Blog",
    description: "Read and write the public blog.",
    scopes: ["blog:read", "blog:write"],
  },
  {
    id: "platform",
    label: "Platform",
    description: "Feature flags and admin accounts.",
    scopes: ["flags:write", "admins:write"],
  },
  {
    id: "developer",
    label: "Developer",
    description: "API keys and the API reference.",
    scopes: ["apikeys:mint", "docs:read"],
  },
];

export function isAdminScope(s: string): s is AdminScope {
  return (ADMIN_SCOPES as readonly string[]).includes(s);
}

/** Superadmins are not enumerated — they match every scope by rank. */
export function adminHasScope(
  admin: { role: "admin" | "moderator"; scopes?: string[] },
  scope: AdminScope,
): boolean {
  if (admin.role === "admin") return true;
  return (admin.scopes ?? []).includes(scope);
}

/**
 * Whose synthetic accounts may this admin purge?
 *
 * Superadmin: anyone's. Scoped admin: only rows they created, which is why
 * `users.synthetic.createdByAdminId` exists at all. A scoped admin without
 * `synthetic:purge` may purge nothing, including their own.
 */
export function canPurgeSynthetic(
  admin: { role: "admin" | "moderator"; scopes?: string[]; _id: string },
  synthetic: { createdByAdminId: string },
): boolean {
  if (admin.role === "admin") return true;
  if (!adminHasScope(admin, "synthetic:purge")) return false;
  return synthetic.createdByAdminId === admin._id;
}

/**
 * Whose synthetic accounts may this admin MODIFY — profile fields, media,
 * prompts, publish state, credentials?
 *
 * Same ownership rule as purge (superadmin: anyone's; scoped admin: only
 * rows they created) but keyed on `synthetic:create`, because creating a
 * profile and iterating on it afterwards are the same job. Purge keeps
 * its own scope: it is the only irreversible action on this surface.
 */
export function canWriteSynthetic(
  admin: { role: "admin" | "moderator"; scopes?: string[]; _id: string },
  synthetic: { createdByAdminId: string },
): boolean {
  if (admin.role === "admin") return true;
  if (!adminHasScope(admin, "synthetic:create")) return false;
  return synthetic.createdByAdminId === admin._id;
}

// ── Deployment environment ────────────────────────────────────────────

export type KeyEnv = "dev" | "live";

/**
 * Which deployment is this code running on?
 *
 * Mirrors lib/envSafety.ts's convention exactly — and depends on it being
 * followed: prod leaves WYDM_DEPLOY_ENV UNSET (or "production"), non-prod
 * deployments set it to dev/development/staging/local. Anything unset is
 * therefore treated as live, which is the safe default in both directions:
 * a prod deployment that forgot the var still refuses dev-stamped keys,
 * and a dev deployment that forgot it refuses dev keys loudly rather than
 * quietly accepting them.
 */
export function deployEnv(): KeyEnv {
  const e = (process.env.WYDM_DEPLOY_ENV ?? "").trim().toLowerCase();
  const nonProd = e === "dev" || e === "development" || e === "staging" || e === "local";
  return nonProd ? "dev" : "live";
}

/**
 * Human-visible prefix on a minted key. The env segment is NOT decoration:
 * `requireAdminKey` rejects a key whose stamp disagrees with `deployEnv()`,
 * so an agent pointed at the wrong deployment fails on its first call with
 * an explicit message instead of silently writing test profiles into
 * production. Keep the two in sync.
 */
export function keyPrefix(env: KeyEnv): string {
  return env === "dev" ? "wydm_admin_dev_" : "wydm_admin_live_";
}

/** Parse the env stamp back out of a presented key. Null when malformed. */
export function envFromKey(secret: string): KeyEnv | null {
  if (secret.startsWith(keyPrefix("dev"))) return "dev";
  if (secret.startsWith(keyPrefix("live"))) return "live";
  return null;
}
